import { Knowledgebase } from '@/models/knowledgebase';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { messageSelectByQuery } from '../thunks/DBServiceThunks';

/**
 * Select the user's query message (first user message in document search chat)
 */
export const selectDocumentSearchUserQuery = createSelector(
  [
    (state: RootState) => state.DBMessageReducer,
    (state: RootState) => state.ChatReducer.currentChatId
  ],
  (messageReducer, chatId) => {
    if (!chatId) return null;
    
    const messages = messageSelectByQuery(messageReducer, { chatId, sender: 'user' });
    // Return first user message (the search query)
    return messages.length > 0 ? messages[0] : null;
  }
);

/**
 * Select assistant message with metadata for document search
 */
export const selectDocumentSearchAssistantMessage = createSelector(
  [
    (state: RootState) => state.DBMessageReducer,
    (state: RootState) => state.ChatReducer.currentChatId
  ],
  (messageReducer, chatId) => {
    if (!chatId) return null;
    
    const messages = messageSelectByQuery(messageReducer, { chatId, sender: 'assistant' });
    // Return first assistant message (the search response)
    return messages.length > 0 ? messages[0] : null;
  }
);

/**
 * Select metadata from assistant message (queryId, status, selectedKnowledgebaseIds, searchType)
 */
export const selectDocumentSearchMetadata = createSelector(
  [selectDocumentSearchAssistantMessage],
  (assistantMessage) => {
    return assistantMessage?.metadata || null;
  }
);

/**
 * Select knowledgebase entities by IDs from metadata
 */
export const selectDocumentSearchKnowledgebases = createSelector(
  [
    selectDocumentSearchMetadata,
    (state: RootState) => state.DBKnowledgebaseReducer.entities
  ],
  (metadata, knowledgebaseEntities) => {
    if (!metadata?.selectedKnowledgebaseIds) return [];
    
    const knowledgebases: Knowledgebase[] = [];
    
    metadata.selectedKnowledgebaseIds.forEach(kbId => {
      const kb = knowledgebaseEntities[kbId];
      if (kb) {
        knowledgebases.push(kb as Knowledgebase);
      }
    });
    
    return knowledgebases;
  }
);

/**
 * Select current status of document search ('active' | 'completed' | 'error' | 'none')
 */
export const selectDocumentSearchStatus = createSelector(
  [selectDocumentSearchMetadata],
  (metadata) => {
    if (!metadata) return 'none';
    return metadata.status || 'none';
  }
);

/**
 * Select queryId for progress tracking
 */
export const selectDocumentSearchQueryId = createSelector(
  [selectDocumentSearchMetadata],
  (metadata) => {
    return metadata?.queryId || null;
  }
);

